import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { CharacterTabStrip, type CharacterTabItem } from '../components/bosses/CharacterTabStrip';
import { CharacterPitchedList } from '../components/pitched/CharacterPitchedList';
import { PitchedOverview, type PitchedOverviewCharacter } from '../components/pitched/PitchedOverview';
import { useSetPitchedDropCount, useSetPitchedObtained, usePitchedForAccount } from '../lib/pitched';
import { useAccounts } from '../lib/roster';
import { classDisplayName, useBossCatalog, useClasses, usePitchedItemCatalog } from '../lib/reference';
import type { PitchedItemDto } from '../lib/reference';

/**
 * `/pitched` — the account-wide Pitched/Brilliant Boss Set item tracker (REV6 —
 * docs/specs/REV6-pitched-tracker.md). Same character-tab-strip + trailing "Overview" tab pattern
 * as `/bosses` (reuses `CharacterTabStrip` directly rather than forking a third tab-strip recipe —
 * the REV6 spec explicitly calls this out).
 */
export function Pitched() {
  const [searchParams, setSearchParams] = useSearchParams();

  const accountsQuery = useAccounts();
  const bossCatalogQuery = useBossCatalog();
  const itemCatalogQuery = usePitchedItemCatalog();
  // REV18: friendly class names in the tab microtext instead of raw slugs.
  const classesQuery = useClasses();
  const classes = classesQuery.data ?? [];
  const accounts = accountsQuery.data ?? [];

  // REV14: account selection lives in `?account=`, not `useState` — mirrors Links.tsx's
  // requested -> valid-set -> first-account fallback exactly (see Bosses.tsx for the twin copy of
  // this logic and why it matters for cross-account command-palette jumps).
  const accountParam = searchParams.get('account');
  const requestedAccountId = accountParam !== null ? Number(accountParam) : null;
  const validAccountIds = new Set(accounts.map((a) => a.id));
  const effectiveAccountId =
    requestedAccountId !== null && validAccountIds.has(requestedAccountId)
      ? requestedAccountId
      : (accounts[0]?.id ?? null);

  const selectAccount = (id: number) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('account', String(id));
      return next;
    });
  };

  const pitchedQuery = usePitchedForAccount(effectiveAccountId);
  const setObtained = useSetPitchedObtained();
  const setDropCount = useSetPitchedDropCount();

  const pendingKey =
    setObtained.isPending && setObtained.variables
      ? `${setObtained.variables.characterId}:${setObtained.variables.slug}`
      : null;
  const pendingDropKey =
    setDropCount.isPending && setDropCount.variables
      ? `${setDropCount.variables.characterId}:${setDropCount.variables.bossSlug}`
      : null;

  const handleToggle = (characterId: number, item: PitchedItemDto, obtained: boolean) => {
    if (effectiveAccountId === null) {
      return;
    }
    setObtained.mutate({ accountId: effectiveAccountId, characterId, slug: item.slug, obtained });
  };

  const handleDropCountChange = (characterId: number, bossSlug: string, count: number) => {
    if (effectiveAccountId === null) {
      return;
    }
    setDropCount.mutate({ accountId: effectiveAccountId, characterId, bossSlug, count });
  };

  if (accountsQuery.isPending || bossCatalogQuery.isPending || itemCatalogQuery.isPending) {
    return <p className="text-sm text-text-muted">Loading the Pitched tracker…</p>;
  }
  if (accountsQuery.isError || bossCatalogQuery.isError || itemCatalogQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load the Pitched tracker. Please retry in a moment.
      </p>
    );
  }
  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        note="Add your main under Characters and let's start tracking Pitched items."
        actionTo="/characters"
        actionLabel="Go to Characters"
      />
    );
  }

  const bossCatalog = bossCatalogQuery.data ?? [];
  const itemCatalog = itemCatalogQuery.data ?? [];
  const effectiveAccount = accounts.find((a) => a.id === effectiveAccountId);
  const rosterCharacters = effectiveAccount?.characters ?? [];
  const obtainedByCharacter = new Map(
    (pitchedQuery.data?.characters ?? []).map((c) => [c.characterId, c.obtainedSlugs]),
  );
  const dropCountsByCharacter = new Map(
    (pitchedQuery.data?.characters ?? []).map((c) => [c.characterId, c.dropCounts]),
  );

  const tabs: CharacterTabItem[] = [
    ...rosterCharacters.map((c) => ({
      id: c.id,
      label: c.name,
      microtext: `${classDisplayName(c.classSlug, classes)} · Lv.${c.level}`,
      avatarUrl: c.avatarUrl,
      accountId: c.accountId,
    })),
    { id: 'overview' as const, label: 'Overview' },
  ];
  const validTabIds = new Set<number | 'overview'>(tabs.map((t) => t.id));

  const characterParam = searchParams.get('character');
  const requestedTabId: number | 'overview' | null =
    characterParam === 'overview' ? 'overview' : characterParam !== null ? Number(characterParam) : null;
  const selectedTabId: number | 'overview' =
    requestedTabId !== null && validTabIds.has(requestedTabId)
      ? requestedTabId
      : (rosterCharacters[0]?.id ?? 'overview');

  const selectTab = (id: number | 'overview') => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('character', String(id));
      return next;
    });
  };

  const overviewCharacters: PitchedOverviewCharacter[] = rosterCharacters.map((c) => ({
    characterId: c.id,
    accountId: c.accountId,
    name: c.name,
    avatarUrl: c.avatarUrl,
    obtainedSlugs: obtainedByCharacter.get(c.id) ?? [],
    dropCounts: dropCountsByCharacter.get(c.id) ?? [],
  }));

  return (
    <div className="stagger-children space-y-5">
      <div className="space-y-4 rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
        <div>
          <h1 className="font-display text-xl font-semibold text-text">Pitched</h1>
          <p className="mt-1 text-sm text-text-muted">
            Which Pitched and Brilliant Boss Set items each character has obtained.
          </p>
        </div>

        {accounts.length > 1 && (
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Account">
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                role="tab"
                aria-selected={account.id === effectiveAccountId}
                onClick={() => selectAccount(account.id)}
                className={[
                  'min-h-tap rounded-full px-4 text-sm font-semibold transition-colors',
                  account.id === effectiveAccountId
                    ? 'bg-accent text-accent-contrast'
                    : 'bg-bg-elevated-hover text-text-muted hover:bg-border-strong',
                ].join(' ')}
              >
                {account.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <CharacterTabStrip tabs={tabs} selectedId={selectedTabId} onSelect={selectTab} />

      {pitchedQuery.isPending ? (
        <p className="text-sm text-text-muted">
          Loading {selectedTabId === 'overview' ? 'the account overview' : "this character's Pitched items"}…
        </p>
      ) : pitchedQuery.isError ? (
        <p role="alert" className="text-sm text-danger">
          Could not load Pitched items. Please retry in a moment.
        </p>
      ) : selectedTabId === 'overview' ? (
        <div id="pitched-tabpanel-overview" role="tabpanel" aria-labelledby="pitched-tab-overview">
          <PitchedOverview
            characters={overviewCharacters}
            items={itemCatalog}
            bossCatalog={bossCatalog}
            pendingKey={pendingKey}
            pendingDropKey={pendingDropKey}
            onToggle={(character, item, obtained) => handleToggle(character.characterId, item, obtained)}
            onDropCountChange={(character, bossSlug, count) =>
              handleDropCountChange(character.characterId, bossSlug, count)
            }
          />
        </div>
      ) : (
        <CharacterPitchedList
          characterName={rosterCharacters.find((c) => c.id === selectedTabId)?.name ?? ''}
          characterId={selectedTabId}
          items={itemCatalog}
          bossCatalog={bossCatalog}
          obtainedSlugs={obtainedByCharacter.get(selectedTabId) ?? []}
          pendingSlug={pendingKey?.startsWith(`${selectedTabId}:`) ? (pendingKey.split(':')[1] ?? null) : null}
          onToggle={(item, obtained) => handleToggle(selectedTabId, item, obtained)}
          dropCounts={dropCountsByCharacter.get(selectedTabId) ?? []}
          pendingDropBossSlug={
            pendingDropKey?.startsWith(`${selectedTabId}:`) ? (pendingDropKey.split(':')[1] ?? null) : null
          }
          onDropCountChange={(bossSlug, count) => handleDropCountChange(selectedTabId, bossSlug, count)}
        />
      )}
    </div>
  );
}
